import cloneDeep from "lodash/cloneDeep";
import { getTokenHeader } from "@/helpers/commonHelper";
import axios from "axios";
import { confirmWarningConfig } from "@/configuration/confirmConfigurations";
import { debounce } from "lodash";
import TreeViewSelection from "@/components/Common/TreeViewSelection/TreeViewSelection.vue";
import EventImagePreview from "@/components/Events/EventImagePreview/EventImagePreview.vue";
import ValidationDialog from "@/components/Common/ValidationDialog/ValidationDialog.vue";
import KisList from "@/components/KIS/KisList/KisList.vue";
import KisPersonEditDialog from "@/components/KIS/KisPersonEditDialog/KisPersonEditDialog.vue";
import copy from "copy-to-clipboard";
import KisPersonFilter from "@/components/KIS/KisPersonFilter/KisPersonFilter.vue";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

const defaultItem = { name: "", description: "", address: {}, active: true };

export default {
  name: "KisPersonList",
  components: {
    KisPersonFilter,
    KisPersonEditDialog,
    KisList,
    ValidationDialog,
    EventImagePreview,
    TreeViewSelection
  },
  data: () => ({
    loading: false,
    editDialog: false,
    editedItem: {},
    filter: { name: "", limit: 10 },
    headers: [
      { text: "Name", value: "name" },
      { text: "Dienstleistungen", value: "mappedTasks", sortable: false },
      { text: "Zuletzt geändert am", value: "updatedAt" },
      { text: "Aktiv", value: "active", sortable: false },
      { text: "Aktionen", sortable: false, value: "actions" }
    ]
  }),
  async mounted() {
    this.loading = true;
    const itemsPerPage = this.itemsPerPage;
    if (itemsPerPage && !isNaN(itemsPerPage)) {
      this.filter.limit = itemsPerPage;
      this.filter.itemsPerPage = itemsPerPage;
      this.itemsPerPage = itemsPerPage;
    }
    await this.$store.dispatch("getPersons", this.filter);
    await this.getAllTasks();
    await this.$store.dispatch("getAllUsers");
    await this.$store.dispatch("getSubjectAreas");
    await this.$store.dispatch("getDepartments");

    this.loading = false;
  },
  methods: {
    async getAllTasks() {
      const limit = 100;
      if (!this.taskCount) await this.$store.dispatch("getTasks");
      while (this.taskCount > this.tasks?.length) {
        await this.$store.dispatch("getTasks", {
          skip: this.tasks.length,
          limit,
          getAll: true
        });
      }
    },
    editItem(item) {
      this.editDialog = true;
      this.editedItem = cloneDeep(item);
    },
    addNewPerson() {
      this.editedItem = cloneDeep(defaultItem);
      this.editDialog = true;
    },
    copyItem(item) {
      this.editedItem = cloneDeep(item);
      delete this.editedItem?._id;
      this.editDialog = true;
    },
    setPerson(item) {
      this.editedItem = item;
    },
    closeDialog() {
      this.editedItem = cloneDeep(defaultItem);
      this.editDialog = false;
    },
    async updatePerson(person) {
      try {
        const headers = getTokenHeader();

        const response = await axios.put(
          `${env.kis_api_client_url}person/${person._id}`,
          person,
          {
            params: {
              principal_id: person.principal_id
            },
            headers
          }
        );
        if (response.status === 200) {
          await this.$store.dispatch(
            "createSuccess",
            "Erfolgreich gespeichert!"
          );
          this.setPerson(response.data);
          await this.$store.dispatch("getPersons", this.filter);
        } else {
          console.error(response);
          await this.$store.dispatch(
            "createError",
            "Es ist ein Fehler aufgetreten!"
          );
        }
      } catch (e) {
        console.error(e);
        await this.$store.dispatch("createError");
      }
    },
    async deletePerson(person) {
      try {
        const confirm = await this.$confirm(
          "Möchten Sie diese Person wirklich löschen?",
          confirmWarningConfig
        );
        if (confirm) {
          const headers = getTokenHeader();
          if (!this.editedItem.principal_id)
            this.editedItem.principal_id = this.currentPrincipal;
          const response = await axios.delete(
            `${env.kis_api_client_url}person/${person._id}`,
            {
              params: {
                principal_id: person.principal_id
              },
              headers
            }
          );
          if (response.status === 200) {
            await this.$store.dispatch(
              "createSuccess",
              "Erfolgreich gelöscht!"
            );
            await this.$store.dispatch("getPersons", this.filter);
          } else {
            console.error(response);
            await this.$store.dispatch(
              "createError",
              "Es ist ein Fehler aufgetreten!"
            );
          }
        }
      } catch (e) {
        console.error(e);
        await this.$store.dispatch("createError");
      }
    },
    setFilterPage(page) {
      this.filter.page = page;
    },
    setFilterLimit(limit) {
      this.filter.limit = limit;
      this.filter.itemsPerPage = limit;
      localStorage.setItem("itemsPerPage", limit);
    },

    async getPersons(filter) {
      if (!filter.limit) filter.limit = this.filter.limit;
      if (!filter.page) filter.page = this.filter.page;
      if (!filter.itemsPerPage) filter.itemsPerPage = this.filter.itemsPerPage;
      if (!filter.deleted) filter.deleted = false;

      if (filter.department && Array.isArray(filter.department))
        filter.department = filter.department[0];
      if (filter.subjectArea && Array.isArray(filter.subjectArea))
        filter.subjectArea = filter.subjectArea[0];
      if (filter.task && Array.isArray(filter.task))
        filter.task = filter.task[0];

      await this.$store.dispatch("getPersons", filter ?? this.filter);
    },
    getTaskString(tasks) {
      let additionalString = "";
      if (!tasks?.length) return "";
      tasks = tasks.filter(t => this.tasks.find(task => task._id === t)?._id);
      if (tasks?.length > 3)
        additionalString = ` + ${tasks.length - 3} weitere`;
      tasks = tasks.slice(0, 3);
      return (
        tasks
          ?.map(task => this.tasks.find(t => t._id === task)?.name)
          .join(", ")
          .replace(/ ,/g, "") + additionalString
      );
    },
    applySearchText: debounce(async function() {
      if (this.filter.name.length >= 3 || !this.filter.name.length)
        await this.$store.dispatch("getPersons", this.filter);
    }, 500)
  },
  computed: {
    copy() {
      return copy;
    },
    persons() {
      return this.$store.state.persons?.map(person => ({
        ...person,
        mappedTasks: this.getTaskString(person.tasks)
      }));
    },
    subjectAreas() {
      return this.$store.state.subjectAreas.map(subjectArea => ({
        title: subjectArea.name,
        ...subjectArea
      }));
    },
    departments() {
      return this.$store.state.departments.map(department => ({
        title: department.name,
        ...department
      }));
    },
    tasks() {
      return this.$store.state.tasks.map(
        task => (task = { title: task.name, ...task })
      );
    },
    taskCount() {
      return this.$store.state.taskCount;
    },
    personCount() {
      return this.$store.state.personCount;
    },
    currentPrincipal() {
      return this.$store.state.selectedPrincipal.id;
    }
  }
};
